import {
    TransactionColumn,
    TransactionView,
} from "@common/types/views/transactionViews";
import { CloseTemplateReviewField } from "@common/types/closeTemplateReviewField";

/** A column descriptor produced when injecting a review column. */
type InjectedColumn = TransactionView["columns"][number];

/**
 * Every ledger column variant that maps to the `ledger` review field.
 * Accrual-basis base views carry an `ACCRUAL_LEDGER` (or `ACCRUAL_REVIEW_LEDGER`)
 * column while cash-basis ones carry `LEDGER`; all of them must be stripped
 * before re-appending the basis-aware reviewable ledger so a review table never
 * renders two ledger columns of different bases side-by-side.
 *
 * The `accrual_ledger` review field owns a subset of these (`ACCRUAL_LEDGER` +
 * `ACCRUAL_REVIEW_LEDGER`) independently, so when both fields are active the
 * union of owned ids is still correct and each injects its own column.
 */
const LEDGER_COLUMN_IDS: string[] = [
    TransactionColumn.LEDGER,
    TransactionColumn.ACCRUAL_LEDGER,
    TransactionColumn.ACCRUAL_REVIEW_LEDGER,
];

/** Column ids owned by the `accrual_ledger` review field. */
const ACCRUAL_LEDGER_COLUMN_IDS: string[] = [
    TransactionColumn.ACCRUAL_LEDGER,
    TransactionColumn.ACCRUAL_REVIEW_LEDGER,
];

/** Detects whether the base view was built for an accrual-basis template. */
function isAccrualBasisView(view: TransactionView): boolean {
    return view.columns.some(
        (col) =>
            col.id === TransactionColumn.ACCRUAL_LEDGER ||
            col.id === TransactionColumn.ACCRUAL_REVIEW_LEDGER,
    );
}

/**
 * Resolves the reviewable ledger column for the `ledger` review field.
 *
 * The `ledger` field always marks `TransactionReviewField.CASH_LEDGER`, so its
 * rendered column should reflect the cash ledger. On accrual-basis views it is
 * shown via the dedicated `ACCRUAL_REVIEW_LEDGER` column purely for display
 * convenience — but only while `accrual_ledger` is not independently selected.
 * When `accrual_ledger` is also a review field it owns the accrual column, so
 * `ledger` falls back to the cash `LEDGER` column (in its `review` variant) to
 * avoid two accrual-flavoured columns rendering side-by-side and to keep the
 * displayed column consistent with the field it marks.
 */
function getLedgerReviewColumn(
    view: TransactionView,
    reviewFields: CloseTemplateReviewField[],
): InjectedColumn {
    const hasSeparateAccrualLedgerField =
        reviewFields.includes("accrual_ledger");
    if (isAccrualBasisView(view) && !hasSeparateAccrualLedgerField) {
        return {
            id: TransactionColumn.ACCRUAL_REVIEW_LEDGER,
        };
    }
    return {
        id: TransactionColumn.LEDGER,
        options: {
            customVariants: { variant: "review" },
        },
    };
}

/** Resolves a single review field to the column to inject for it. */
function getReviewColumn(
    field: CloseTemplateReviewField,
    view: TransactionView,
    reviewFields: CloseTemplateReviewField[],
): InjectedColumn {
    if (field === "ledger") {
        return getLedgerReviewColumn(view, reviewFields);
    }
    if (field === "accrual_ledger") {
        // Always injects the accrual ledger column in review variant regardless
        // of template basis, so cash-basis tasks can also have the accrual
        // ledger as a separately reviewable field.
        return {
            id: TransactionColumn.ACCRUAL_LEDGER,
            options: {
                customVariants: { variant: "review" },
            },
        };
    }
    if (field === "entity") {
        return {
            id: TransactionColumn.ENTITY,
            options: {
                customVariants: { variant: "review" },
                // The review variant adds a review button alongside the entity
                // name, so it needs 10 px more horizontal space.
                minSize: 92,
                maxSize: 92,
            },
        };
    }
    if (field === "class") {
        return {
            id: TransactionColumn.CLASSES,
            options: {
                customVariants: { variant: "review" },
            },
        };
    }
    if (field === "note") {
        return {
            id: TransactionColumn.NOTE,
            options: {
                customVariants: { variant: "review" },
            },
        };
    }
    return {
        id:
            field === "counterparty"
                ? TransactionColumn.COUNTERPARTY
                : TransactionColumn.CATEGORY,
        options: {
            customVariants: { variant: "review" },
        },
    };
}

/**
 * Collects the column ids each review field "owns" so they can be stripped
 * from the base view before re-appending. The `ledger` field owns every ledger
 * variant because the base view carries a basis-specific ledger column that
 * must be replaced by the single reviewable ledger column.
 */
function getOwnedColumnIds(
    reviewFields: CloseTemplateReviewField[],
): Set<string> {
    const ownedColumnIds = new Set<string>();
    for (const field of reviewFields) {
        if (field === "ledger") {
            for (const id of LEDGER_COLUMN_IDS) {
                ownedColumnIds.add(id);
            }
        } else if (field === "accrual_ledger") {
            for (const id of ACCRUAL_LEDGER_COLUMN_IDS) {
                ownedColumnIds.add(id);
            }
        } else if (field === "counterparty") {
            ownedColumnIds.add(TransactionColumn.COUNTERPARTY);
        } else if (field === "entity") {
            ownedColumnIds.add(TransactionColumn.ENTITY);
        } else if (field === "class") {
            ownedColumnIds.add(TransactionColumn.CLASSES);
        } else if (field === "note") {
            ownedColumnIds.add(TransactionColumn.NOTE);
        } else {
            ownedColumnIds.add(TransactionColumn.CATEGORY);
        }
    }
    return ownedColumnIds;
}

/**
 * Marks every column owned by the given `reviewFields` — plus the
 * `TransactionColumn.REVIEW` indicator column — as `required` in a ViewConfig
 * `columns` record. ColumnSettings then prevents hiding them while still
 * allowing pin and reorder (`immutable` would lock those too).
 *
 * Shared by `TransactionReviewTaskBody` and `CloseTemplateTaskTransactionsPreview`
 * so both call sites stay in lockstep, mirroring how `injectReviewColumns` /
 * `stripReviewColumns` are already shared.
 */
export function markReviewColumnsRequired<
    TConfig extends { required?: boolean; immutable?: boolean },
>(
    columns: Record<string, TConfig>,
    reviewFields: CloseTemplateReviewField[],
): Record<string, TConfig> {
    const requiredIds = getOwnedColumnIds(reviewFields);
    requiredIds.add(TransactionColumn.REVIEW);
    return Object.fromEntries(
        Object.entries(columns).map(([id, config]) => [
            id,
            requiredIds.has(id)
                ? { ...config, required: true, immutable: false }
                : config,
        ]),
    );
}

/**
 * Detects a column that `injectReviewColumns` appended: a review-variant column
 * (marked via `customVariants.variant`), the basis-aware review ledger
 * (`ACCRUAL_REVIEW_LEDGER`, only ever produced by injection), or the
 * `TransactionColumn.REVIEW` indicator. Base context columns
 * (DATE/DESCRIPTION/CATEGORY/ledger/AMOUNT) never carry these markers.
 */
function isInjectedReviewColumn(
    column: TransactionView["columns"][number],
): boolean {
    return (
        column.id === TransactionColumn.REVIEW ||
        column.id === TransactionColumn.ACCRUAL_REVIEW_LEDGER ||
        column.options?.customVariants?.variant === "review"
    );
}

/**
 * Removes only the columns that `injectReviewColumns` previously appended,
 * regardless of which review fields produced them, while preserving every base
 * context column (DATE/DESCRIPTION/CATEGORY/ledger/AMOUNT) — even the
 * `CATEGORY`/`LEDGER` columns that overlap review-field-owned ids.
 *
 * Use this (rather than `stripReviewColumns(view, ALL_REVIEW_FIELDS)`) when
 * recomputing a stored view after the review-field selection changes, so
 * deselecting a field never drops a default context column that shares its id.
 */
export function stripInjectedReviewColumns(
    view: TransactionView,
): TransactionView {
    return {
        ...view,
        columns: view.columns.filter((col) => !isInjectedReviewColumn(col)),
    };
}

/**
 * Removes every column that `injectReviewColumns` injects for the given
 * `reviewFields` — the basis-aware reviewable ledger, the review-variant
 * columns, and the `TransactionColumn.REVIEW` indicator — so a base
 * `TransactionView` can be persisted without the render-time review columns
 * leaking into storage.
 *
 * This is the inverse of the append step in `injectReviewColumns` and reuses
 * the same ownership logic (`getOwnedColumnIds` + the `REVIEW` exclusion), so
 * the two stay in lockstep as the single source of truth. `strip` is idempotent.
 * After strip, inject appends missing review columns; column order may differ
 * from injecting a view that already had those columns in place.
 */
export function stripReviewColumns(
    view: TransactionView,
    reviewFields: CloseTemplateReviewField[],
): TransactionView {
    const ownedColumnIds = getOwnedColumnIds(reviewFields);
    return {
        ...view,
        columns: view.columns.filter(
            (col) =>
                !ownedColumnIds.has(col.id) &&
                col.id !== TransactionColumn.REVIEW,
        ),
    };
}

/**
 * Copies pin and size from an existing column onto the desired review-column
 * descriptor so re-injection (drawer open, review-field edits) does not reset
 * layout the user already saved.
 */
function withPreservedLayout(
    existing: InjectedColumn,
    desired: InjectedColumn,
): InjectedColumn {
    const pin = existing.options?.pin;
    const size = existing.options?.size;
    if (pin === undefined && size === undefined) {
        return desired;
    }
    return {
        id: desired.id,
        options: {
            ...desired.options,
            ...(pin === undefined ? {} : { pin }),
            ...(size === undefined ? {} : { size }),
        },
    };
}

/**
 * Syncs a `TransactionView` with `reviewFields`:
 *
 * - Existing review columns keep their position, pin, and size.
 * - Missing review columns (and `TransactionColumn.REVIEW`) are appended.
 * - Review columns for deselected fields, and ledger variants replaced by the
 *   basis-aware review ledger, are removed so they are never duplicated.
 *
 * Used by `TransactionReviewTaskBody` and `CloseTemplateTaskTransactionsPreview`
 * (via the stored base view / `withReviewFieldsAndColumns`).
 */
export function injectReviewColumns(
    view: TransactionView,
    reviewFields: CloseTemplateReviewField[],
): TransactionView {
    const desiredColumns: InjectedColumn[] = [
        ...reviewFields.map((field) =>
            getReviewColumn(field, view, reviewFields),
        ),
        { id: TransactionColumn.REVIEW },
    ];
    const desiredById = new Map(
        desiredColumns.map((column) => [column.id, column]),
    );
    const ownedColumnIds = getOwnedColumnIds(reviewFields);

    const nextColumns: InjectedColumn[] = [];
    const placedDesiredIds = new Set<string>();

    for (const column of view.columns) {
        const desired = desiredById.get(column.id);
        if (desired) {
            nextColumns.push(withPreservedLayout(column, desired));
            placedDesiredIds.add(column.id);
            continue;
        }
        if (isInjectedReviewColumn(column) || ownedColumnIds.has(column.id)) {
            continue;
        }
        nextColumns.push(column);
    }

    for (const desired of desiredColumns) {
        if (!placedDesiredIds.has(desired.id)) {
            nextColumns.push(desired);
        }
    }

    return {
        ...view,
        columns: nextColumns,
    };
}
